import React from "react";
import { cn } from "@/lib/utils";

type PhoneSize = "sm" | "md" | "lg";

interface PhoneMockupProps {
    children?: React.ReactNode;
    imageSrc?: string;
    size?: PhoneSize;
    className?: string;
}

const sizeMap: Record<PhoneSize, { w: string; h: string; radiusOuter: string; radiusInner: string }> = {
    sm: { w: "w-[160px]", h: "h-[320px]", radiusOuter: "rounded-[2rem]", radiusInner: "rounded-[1.625rem]" },
    md: { w: "w-[220px]", h: "h-[440px]", radiusOuter: "rounded-[2.5rem]", radiusInner: "rounded-[2.125rem]" },
    lg: { w: "w-[280px]", h: "h-[560px]", radiusOuter: "rounded-[3rem]", radiusInner: "rounded-[2.625rem]" },
};

export const PhoneMockup = ({ children, imageSrc, size = "md", className }: PhoneMockupProps) => {
    const s = sizeMap[size];
    return (
        <div 
            className={cn(
                "relative shrink-0 p-[6px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] ring-1 ring-white/10",
                "bg-gradient-to-b from-neutral-700 via-neutral-900 to-neutral-800", // Titanium frame
                s.w, s.h, s.radiusOuter, 
                className
            )}
            style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "translateZ(0)" }}
        >
            {/* Glossy bezel highlight */}
            <div className="absolute inset-0 z-10 opacity-30 pointer-events-none overflow-hidden rounded-[inherit]">
                <div className="absolute -top-[100%] -left-[100%] w-[300%] h-[300%] bg-[linear-gradient(45deg,transparent_45%,rgba(255,255,255,0.1)_50%,transparent_55%)] animate-pulse" />
            </div>

            {/* Inner frame depth */}
            <div className="absolute inset-[1px] rounded-[inherit] ring-1 ring-black/60 z-0" />
            <div className="absolute inset-[2px] rounded-[inherit] ring-1 ring-white/5 z-0" />

            {/* Side buttons - Metallic gradient */}
            <span className="absolute -right-[2px] top-24 h-16 w-[3px] rounded-r bg-gradient-to-b from-neutral-600 to-neutral-800 border-r border-white/10" />
            <span className="absolute -left-[2px] top-20 h-10 w-[3px] rounded-l bg-gradient-to-b from-neutral-600 to-neutral-800 border-l border-white/10" />
            <span className="absolute -left-[2px] top-32 h-14 w-[3px] rounded-l bg-gradient-to-b from-neutral-600 to-neutral-800 border-l border-white/10" />

            {/* Screen Container */}
            <div className={cn(
                "relative h-full w-full overflow-hidden bg-[#0a0a14] ring-2 ring-black/80 shadow-[inset_0_0_12px_rgba(0,0,0,0.4)]",
                s.radiusInner
            )}>
                {/* Dynamic Island */}
                <div className="absolute left-1/2 top-3 z-30 h-[5%] w-[30%] -translate-x-1/2 rounded-full bg-black flex items-center justify-end px-2 gap-1">
                    <div className="w-1 h-1 rounded-full bg-blue-500/20" />
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/20" />
                </div>

                <div className="relative h-full w-full">
                    {imageSrc ? (
                        <img 
                            src={imageSrc} 
                            alt="Phone screen" 
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    ) : (
                        children
                    )}
                </div>
            </div>
        </div>
    );
};

export default PhoneMockup;
